import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight, Users, X } from '../../../icons';
import { Checkbox } from '../Checkbox';
import { SearchInput } from '../Input';
import {
  countPeople, filterGroups, isPicked, peopleOf, togglePicked,
  type Picked, type PickerGroup, type PickerPerson,
} from './entity-picker-core';
import './EntityPicker.css';

/**
 * EntityPicker — 사람을 고르는 한 벌 (디자인 정본 B11).
 *
 * ★★★ **조직도가 기본이고 검색은 그 안에서 한다.** 지금 사람을 고르는 자리 다섯 곳
 *    (결재선·메일 받는사람·새 채팅·채팅방 초대·급여 대상자)은 전부 **이름을 쳐야** 목록이 생기는
 *    자동조회다. 그런데 **결재선은 「누구에게 올려야 하는지」를 «조직으로» 판단하는 일**이라
 *    이름부터 치게 하는 것은 순서가 뒤바뀐 것이다. 여기서는 **아무것도 안 쳐도 전원이 보인다.**
 *
 * ★★★ **동명이인은 «트리 위치»로 갈린다.** 자동조회 목록에서 「김서연」이 둘이면 누가 누군지
 *    알 수 없지만, 조직도에서는 어느 팀 아래 있는지가 그대로 보인다.
 *
 * ★★ **사람만이 아니라 «팀째 · 부서째»도 고른다.** 「기획팀 전원에게 참조」는 한 번에 되어야 한다.
 *
 * ★★ **데이터는 props 로 받는다.** 이 컴포넌트는 서버를 모른다 — 화면이 트리를 넘겨준다.
 *    (지금 `OrgDirectoryModal` 이 트리를 열 때 한 번 읽어 두므로 그 캐시를 나눠 쓰면 된다.
 *    ⚠ 다만 그 적재가 `getTeam()` 을 팀 수만큼 부르는 N+1 이라, **팀이 많아지면 거기서 느려진다** —
 *    집계 엔드포인트는 별도 판이다.)
 */
export interface EntityPickerProps {
  groups: ReadonlyArray<PickerGroup>;
  /** 묶음에 안 속한 사람들(대표 직속 등). */
  loose?: ReadonlyArray<PickerPerson>;
  picked: ReadonlyArray<Picked>;
  onPickedChange: (next: Picked[]) => void;
  multiple?: boolean;
  /** 팀·부서째 고르기를 끈다(사람만 골라야 하는 자리). */
  groupSelectable?: boolean;
  loading?: boolean;
  /** 「담기」를 눌렀을 때. 안 주면 확정 줄을 안 그린다(바로 반영되는 자리). */
  onConfirm?: () => void;
  onCancel?: () => void;
  emptyHint?: ReactNode;
  className?: string;
}

export function EntityPicker({
  groups,
  loose = [],
  picked,
  onPickedChange,
  multiple = true,
  groupSelectable = true,
  loading = false,
  onConfirm,
  onCancel,
  emptyHint = '고를 사람이 없습니다',
  className,
}: EntityPickerProps) {
  const [q, setQ] = useState('');
  /**
   * ★★★ **처음부터 «다» 펼쳐 둔다.** 규격의 요점이 「아무것도 안 쳐도 전원이 보인다」인데,
   *    윗가지만 펼치면 사람은 한 명도 안 보인다(부서 아래 팀, 팀 아래 사람이라 두 단이 접힌다).
   *    처음에 `groups.map(g => g.id)` 로만 채웠다가 검사가 잡았다.
   */
  const [open, setOpen] = useState<Set<string>>(() => {
    const ids = new Set<string>();
    const walk = (list: ReadonlyArray<PickerGroup>) => {
      for (const g of list) { ids.add(g.id); walk(g.children ?? []); }
    };
    walk(groups);
    return ids;
  });

  /**
   * ★★★ **나중에 도착한 트리도 펼친다** (2026-09-22 additive).
   *
   * 위 `useState` 초기화 함수는 **첫 렌더의 `groups` 를 한 번만** 읽는다. 그래서 트리를 «나중에»
   * 받아 넣는 화면(조직도는 3+N 호출이라 게으르게 읽는 자리가 있다)에서는 펼침 집합이 **빈 채로
   * 굳어 사람이 한 줄도 안 보인다** — 이 부품의 요점인 「아무것도 안 쳐도 전원이 보인다」가 깨진다.
   * 바로 위 주석이 적어 둔 「윗가지만 펼쳤다가 검사가 잡았다」와 **같은 자리의 다른 판**이다.
   *
   * ⛔ **사람이 «접은» 가지를 다시 펼치지 않는다.** 그래서 «본 적 있는 id» 를 따로 기억하고,
   *   **처음 보는 id 만** 펼친다. 이것이 없으면 부모가 리렌더될 때마다 접어 둔 가지가 되살아난다.
   */
  const seenGroupIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    const ids: string[] = [];
    const walk = (list: ReadonlyArray<PickerGroup>) => {
      for (const g of list) { ids.push(g.id); walk(g.children ?? []); }
    };
    walk(groups);

    const fresh = ids.filter((id) => !seenGroupIds.current.has(id));
    for (const id of ids) seenGroupIds.current.add(id);
    if (fresh.length === 0) return;   // ★ 처음 보는 가지가 없으면 «아무것도 하지 않는다»

    setOpen((prev) => {
      const next = new Set(prev);
      for (const id of fresh) next.add(id);
      return next;
    });
  }, [groups]);

  /* ★ 검색은 «받아 둔 트리 안에서» — 서버 요청 0. */
  const shown = useMemo(() => filterGroups(groups, q), [groups, q]);
  const shownLoose = useMemo(
    () => (q.trim() ? loose.filter((p) => [p.name, p.sub, p.keywords].filter(Boolean).join(' ').toLowerCase().includes(q.toLowerCase().trim())) : loose),
    [loose, q],
  );
  const total = useMemo(() => countPeople(groups) + loose.length, [groups, loose]);

  const toggle = (next: Picked) => onPickedChange(togglePicked(picked, next, multiple));

  const renderPerson = (p: PickerPerson, depth: number) => (
    <div key={`p-${p.id}`} className="wc-epick__row wc-epick__row--person" style={{ paddingLeft: 10 + depth * 14 }}>
      <Checkbox
        checked={isPicked(picked, 'person', p.id)}
        disabled={p.disabled}
        onChange={() => toggle({ kind: 'person', id: p.id, name: p.name, sub: p.sub })}
        aria-label={`${p.name}${p.sub ? ` ${p.sub}` : ''} 고르기`}
      />
      <span className="wc-epick__name">{p.name}</span>
      {/* ★ 소속·직위를 늘 함께 보인다 — 동명이인을 가르는 유일한 단서다. */}
      {p.sub && <span className="wc-epick__sub">{p.sub}</span>}
    </div>
  );

  const renderGroup = (g: PickerGroup, depth: number): ReactNode => {
    const isOpen = q.trim() ? true : open.has(g.id);   /* 검색 중에는 다 펼쳐 둔다 */
    const kids = peopleOf(g);
    return (
      <div key={`g-${g.id}`} className="wc-epick__group">
        <div className="wc-epick__row wc-epick__row--group" style={{ paddingLeft: 10 + depth * 14 }}>
          <button
            type="button"
            className="wc-epick__caret"
            aria-label={isOpen ? '접기' : '펼치기'}
            onClick={() => setOpen((s) => { const n = new Set(s); n.has(g.id) ? n.delete(g.id) : n.add(g.id); return n; })}
          >
            {isOpen ? <ChevronDown aria-hidden /> : <ChevronRight aria-hidden />}
          </button>
          {groupSelectable ? (
            <Checkbox
              checked={isPicked(picked, 'group', g.id)}
              onChange={() => toggle({ kind: 'group', id: g.id, name: g.name, sub: `${kids.length}명` })}
              aria-label={`${g.name} 전체 고르기`}
            />
          ) : (
            <Users className="wc-epick__gicon" aria-hidden />
          )}
          <span className="wc-epick__gname">{g.name}</span>
          <span className="wc-epick__count">{kids.length}</span>
        </div>
        {isOpen && (
          <>
            {(g.people ?? []).map((p) => renderPerson(p, depth + 1))}
            {(g.children ?? []).map((c) => renderGroup(c, depth + 1))}
          </>
        )}
      </div>
    );
  };

  const nothing = !loading && shown.length === 0 && shownLoose.length === 0;

  return (
    <div className={['wc-epick', className ?? ''].filter(Boolean).join(' ')}>
      <div className="wc-epick__search">
        <SearchInput
          size="sm"
          fullWidth
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onClear={() => setQ('')}
          placeholder={`이름 · 부서 · 직위로 좁히기 (전원 ${total}명)`}
          aria-label="조직도 안에서 찾기"
        />
      </div>

      <div className="wc-epick__tree" role="group" aria-label="조직도">
        {loading ? (
          <p className="wc-epick__msg">불러오는 중…</p>
        ) : nothing ? (
          <p className="wc-epick__msg">{q.trim() ? '찾는 사람이 없습니다' : emptyHint}</p>
        ) : (
          <>
            {shown.map((g) => renderGroup(g, 0))}
            {shownLoose.map((p) => renderPerson(p, 0))}
          </>
        )}
      </div>

      {picked.length > 0 && (
        <div className="wc-epick__picked" aria-label="담은 대상">
          {picked.map((p) => (
            <span key={`${p.kind}-${p.id}`} className="wc-epick__chip">
              {p.kind === 'group' && <Users className="wc-epick__chip-icon" aria-hidden />}
              {p.name}
              {p.sub && <span className="wc-epick__chip-sub">{p.sub}</span>}
              <button type="button" className="wc-epick__chip-x" aria-label={`${p.name} 빼기`}
                onClick={() => toggle(p)}>
                <X aria-hidden />
              </button>
            </span>
          ))}
        </div>
      )}

      {(onConfirm || onCancel) && (
        <div className="wc-epick__foot">
          <span className="wc-epick__foot-count">
            {picked.length > 0 ? `${picked.length}개 담김` : '아직 고르지 않았습니다'}
          </span>
          {onCancel && <button type="button" className="wc-epick__btn" onClick={onCancel}>취소</button>}
          {onConfirm && (
            <button type="button" className="wc-epick__btn wc-epick__btn--primary"
              disabled={picked.length === 0} onClick={onConfirm}>담기</button>
          )}
        </div>
      )}
    </div>
  );
}
